import React, { useState, useEffect } from 'react';
import { auth, db } from '../firebase/config';
import { doc, setDoc, GeoPoint, getDoc, Timestamp } from 'firebase/firestore';
import { addLog, getSettings } from '../services/firestoreCollections';

function VehicleForm() {
  const [plate, setPlate] = useState('');
  const [duration, setDuration] = useState('1');
  const [vehicleType, setVehicleType] = useState('auto'); // Nuevo estado para tipo de vehículo
  const [location, setLocation] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [prices, setPrices] = useState({
    auto: 0.40,
    moto: 0.25,
    exonerado: 0.00
  });
  const [ticketPeriod, setTicketPeriod] = useState({
    start: '',
    end: ''
  });
  const [coordinates, setCoordinates] = useState(null);

  // Actualizar el período del ticket cuando cambie la duración
  useEffect(() => {
    updateTicketPeriod(parseInt(duration));
  }, [duration]);

  // Cargar la configuración de precios
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const settingsDoc = await getSettings();
        if (settingsDoc.exists()) {
          const settings = settingsDoc.data();
          setPrices({
            auto: settings.carHourlyRate || 0.40,
            moto: settings.motorcycleHourlyRate || 0.25,
            exonerado: 0.00
          });
        }
      } catch (err) {
        console.error("Error al cargar configuración:", err);
      }
    };

    loadSettings();
  }, []);

  // Obtener coordenadas geográficas al cargar el componente
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setCoordinates({ latitude, longitude });
          setLocation(`${latitude.toFixed(6)}, ${longitude.toFixed(6)}`);
          console.log("Coordenadas capturadas:", latitude, longitude);
        },
        (error) => {
          console.error("Error al obtener ubicación:", error);
        },
        { enableHighAccuracy: true }
      );
    } else {
      console.warn("Geolocalización no soportada en este navegador");
    }
  }, []);

  // Función para calcular y actualizar el período del ticket
  const updateTicketPeriod = (hours) => {
    const now = new Date();
    const endTime = new Date(now);
    endTime.setHours(now.getHours() + hours);

    // Formatear las horas para mostrar en el formato HH:MM
    const formatTime = (date) => {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    setTicketPeriod({
      start: formatTime(now),
      end: formatTime(endTime),
      startDate: now,
      endDate: endTime
    });
  };

  // Función para actualizar coordenadas manualmente
  const updateLocation = () => {
    if (navigator.geolocation) {
      setLocation("Obteniendo ubicación...");
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setCoordinates({ latitude, longitude });
          setLocation(`${latitude.toFixed(6)}, ${longitude.toFixed(6)}`);
        },
        (error) => {
          console.error("Error al obtener ubicación:", error);
          setLocation("");
        }
      );
    }
  };

  // Obtener el precio actual según el tipo de vehículo
  const getCurrentPrice = () => {
    return prices[vehicleType] || 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      const user = auth.currentUser;
      
      // Obtener IP del cliente
      const clientIP = '192.168.1.1'; // En producción esto vendría de una API o servicio
      
      // Generar fecha del documento (DD-MM-YYYY) como campo
      const now = new Date();
      const dd = now.getDate().toString().padStart(2, '0');
      const mm = (now.getMonth() + 1).toString().padStart(2, '0');
      const yyyy = now.getFullYear();
      const fechaDocumento = `${dd}-${mm}-${yyyy}`;
      
      // Obtener el numberCa del usuario
      let numberCa = 'N/A';
      try {
        const userDocRef = doc(db, 'usuarios', user.email);
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          numberCa = userDoc.data().numberCa || 'N/A';
        }
      } catch (err) {
        console.error("Error al obtener numberCa:", err);
      }
      
      // Generar un ID único para este ticket
      const ticketId = `TICKET-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
      
      // Calcular la hora fin basada en la duración
      const endTime = new Date(now.getTime() + parseInt(duration) * 60 * 60 * 1000);

      // Crear GeoPoint si tenemos coordenadas disponibles
      const locationData = coordinates 
        ? new GeoPoint(coordinates.latitude, coordinates.longitude) 
        : location;
      
      // Calcular el precio basado en el tipo de vehículo
      const currentPrice = getCurrentPrice();
      const totalPrice = currentPrice * parseInt(duration);
      
      // Agregar ticket a Firestore con tipo de vehículo
      const ticketData = {
        id: ticketId,
        fechaDocumento: fechaDocumento,
        plate: plate.toUpperCase(),
        vehicleType: vehicleType, // Nuevo campo para tipo de vehículo
        duration: parseInt(duration),
        price: totalPrice,
        location: locationData,
        coordinates: coordinates ? {
          latitude: coordinates.latitude,
          longitude: coordinates.longitude
        } : null,
        paymentMethod: vehicleType === 'exonerado' ? 'exempt' : 'cash',
        status: 'active',
        createdAt: Timestamp.fromDate(now),
        startTime: Timestamp.fromDate(now),
        endTime: Timestamp.fromDate(endTime),
        ticketPeriod: `${ticketPeriod.start} a ${ticketPeriod.end}`,
        vendor: user.displayName || user.email,
        vendorId: user.uid,
        numberCa: numberCa,
        clientIP
      };
      
      // Guardar ticket en Firestore
      const ticketsRef = doc(db, 'tickets', ticketId);
      await setDoc(ticketsRef, ticketData);
      
      // Registrar en logs
      await addLog({
        userId: user.uid,
        action: 'create_ticket',
        detail: `Ticket creado para placa ${plate.toUpperCase()}, duración: ${duration} hora(s), período: ${ticketPeriod.start} a ${ticketPeriod.end}`,
        ipAddress: clientIP,
        resource: 'ticket',
        resourceId: ticketId
      });
      
      // Limpiar formulario
      setPlate('');
      setDuration('1');
      setLocation('');
      setEmail('');
      updateTicketPeriod(1);
      setSuccess(true);
      
      // Ocultar mensaje de éxito después de 3 segundos
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error("Error al registrar vehículo:", err);
      setError('Error al registrar el vehículo. Inténtalo de nuevo.');
    }
    
    setLoading(false);
  };

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <h2 className="text-xl font-semibold mb-6">Registrar Nuevo Vehículo</h2>
      
      {error && <div className="mb-4 bg-red-100 text-red-700 p-3 rounded">{error}</div>}
      {success && (
        <div className="mb-4 bg-green-100 text-green-700 p-3 rounded">
          ¡Ticket registrado exitosamente para el período {ticketPeriod.start} a {ticketPeriod.end}!
        </div>
      )}
      
      <form onSubmit={handleSubmit}>
        <div className="mb-4">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="plate">
            Placa del Vehículo
          </label>
          <input
            id="plate"
            type="text"
            value={plate}
            onChange={(e) => setPlate(e.target.value)}
            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
            placeholder="Ej: ABC-123"
            required
          />
        </div>
        
        {/* Nuevo selector de tipo de vehículo */}
        <div className="mb-4">
          <label className="block text-gray-700 text-sm font-bold mb-2">
            Tipo de Vehículo
          </label>
          <div className="flex space-x-4">
            <label className="inline-flex items-center">
              <input
                type="radio"
                name="vehicleType"
                value="auto"
                checked={vehicleType === 'auto'}
                onChange={() => setVehicleType('auto')}
                className="form-radio h-4 w-4 text-blue-600"
              />
              <span className="ml-2 text-gray-700">Auto (${prices.auto.toFixed(2)}/hora)</span>
            </label>
            <label className="inline-flex items-center">
              <input
                type="radio"
                name="vehicleType"
                value="moto"
                checked={vehicleType === 'moto'}
                onChange={() => setVehicleType('moto')}
                className="form-radio h-4 w-4 text-blue-600"
              />
              <span className="ml-2 text-gray-700">Moto (${prices.moto.toFixed(2)}/hora)</span>
            </label>
            <label className="inline-flex items-center">
              <input
                type="radio"
                name="vehicleType"
                value="exonerado"
                checked={vehicleType === 'exonerado'}
                onChange={() => setVehicleType('exonerado')}
                className="form-radio h-4 w-4 text-blue-600"
              />
              <span className="ml-2 text-gray-700">Exonerado (Gratis)</span>
            </label>
          </div>
        </div>
        
        <div className="mb-4">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="duration">
            Duración
          </label>
          <select
            id="duration"
            value={duration}
            onChange={(e) => setDuration(e.target.value)}
            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
            required
          >
            <option value="1">1 hora ({vehicleType === 'exonerado' ? 'Gratis' : `$${(getCurrentPrice() * 1).toFixed(2)}`})</option>
            <option value="2">2 horas ({vehicleType === 'exonerado' ? 'Gratis' : `$${(getCurrentPrice() * 2).toFixed(2)}`})</option>
          </select>
          <p className="text-sm text-gray-600 mt-1">
            Período del ticket: <span className="font-medium">{ticketPeriod.start} a {ticketPeriod.end}</span>
          </p>
        </div>

        <div className="mb-4">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="location">
            Ubicación (Coordenadas)
          </label>
          <div className="flex">
            <input
              id="location"
              type="text"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              placeholder="Coordenadas GPS"
              readOnly
            />
            <button
              type="button"
              onClick={updateLocation}
              className="ml-2 bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold py-2 px-3 rounded"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
          {coordinates && (
            <p className="text-xs text-gray-500 mt-1">
              Latitud: {coordinates.latitude.toFixed(6)}, Longitud: {coordinates.longitude.toFixed(6)}
            </p>
          )}
        </div>
        
        <div className="mb-6">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="email">
            Email (Opcional)
          </label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
            placeholder="Ej: usuario@ejemplo.com"
          />
        </div>
        
        <div className="flex items-center justify-between">
          <button
            type="submit"
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline w-full"
            disabled={loading}
          >
            {loading ? 'Registrando...' : 'Registrar Vehículo'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default VehicleForm;
